'use client'

import { motion } from 'framer-motion'
import { useId, useMemo } from 'react'

import { HOME_EASE } from '@/features/home/components/home-motion'
import { cn } from '@/lib/utils'

const WIDTH = 600
const HEIGHT = 160
const PAD = 6
// Same floor as Account Cards' sparkline: the axis never zooms tighter than 1% of the value, so
// a -$1 wiggle on a large portfolio stays a wiggle instead of filling the chart.
const MIN_SPAN_RATIO = 0.01

function buildPaths(values: readonly number[]) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  const center = (min + max) / 2
  const span = Math.max(max - min, Math.abs(center) * MIN_SPAN_RATIO) || 1
  const half = (span / 2) * 1.1
  const x = (i: number) => PAD + (i / (values.length - 1)) * (WIDTH - PAD * 2)
  const y = (v: number) => PAD + (1 - (v - (center - half)) / (half * 2)) * (HEIGHT - PAD * 2)
  const points = values.map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`)
  const line = `M${points.join('L')}`
  const area = `${line}L${x(values.length - 1).toFixed(2)},${HEIGHT}L${x(0).toFixed(2)},${HEIGHT}Z`
  return { line, area, endX: x(values.length - 1), endY: y(values[values.length - 1]) }
}

/**
 * The equity curve: draws itself in from the left whenever the window changes (`drawKey`),
 * filled with a fade of the trend's color and ending on a beating dot at the live value.
 */
export function HomeSparkline({
  values,
  drawKey,
  className,
}: {
  values: readonly number[]
  drawKey: string
  className?: string
}) {
  const gradientId = useId()
  const paths = useMemo(() => (values.length >= 2 ? buildPaths(values) : null), [values])
  if (!paths) return null
  const rising = values[values.length - 1] >= values[0]

  return (
    <div
      className={cn(
        'relative h-full w-full',
        rising ? 'text-chart-mint' : 'text-chart-red',
        className
      )}
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="h-full w-full overflow-visible"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          key={`area-${drawKey}`}
          d={paths.area}
          fill={`url(#${gradientId})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.25, ease: HOME_EASE }}
        />
        <motion.path
          key={`line-${drawKey}`}
          d={paths.line}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, ease: HOME_EASE }}
        />
      </svg>
      {/* An HTML dot, not an SVG circle: the stretched viewBox would make a circle an oval. */}
      <motion.span
        key={`dot-${drawKey}`}
        aria-hidden="true"
        className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current motion-safe:animate-[conn-pulse_2s_infinite]"
        style={{ left: `${(paths.endX / WIDTH) * 100}%`, top: `${(paths.endY / HEIGHT) * 100}%` }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.85, type: 'spring', stiffness: 520, damping: 22 }}
      />
    </div>
  )
}
